import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import type { Producto } from '../types/product.types';

export interface ProductReminder {
  productId: string;
  productSlug: string;
  productName: string;
  addedAt: Date;
  userEmail?: string;
  userPhone?: string;
}

export interface ProductViewLog {
  productId: string;
  productSlug: string;
  productName: string;
  viewedAt: Date;
  viewCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductTrackingService {
  private readonly REMINDERS_STORAGE_KEY = 'allas_product_reminders';
  private readonly VIEWS_STORAGE_KEY = 'allas_product_views';

  private remindersSubject = new BehaviorSubject<ProductReminder[]>([]);
  private viewsSubject = new BehaviorSubject<ProductViewLog[]>([]);

  constructor() {
    this.loadFromStorage();
  }

  // ===== RECORDATORIOS =====

  /**
   * Agrega un recordatorio para un producto
   */
  addReminder(
    product: Producto,
    userContact?: { email?: string; phone?: string }
  ): boolean {
    const productId = this.getProductUniqueId(product);
    const existingReminder = this.getReminderByProductId(productId);

    if (existingReminder) {
      return false; // Ya existe un recordatorio
    }

    const reminder: ProductReminder = {
      productId: productId,
      productSlug: this.generateProductSlug(product),
      productName: this.getComposedProductName(product),
      addedAt: new Date(),
      userEmail: userContact?.email,
      userPhone: userContact?.phone,
    };

    const currentReminders = this.remindersSubject.value;
    const updatedReminders = [...currentReminders, reminder];

    this.remindersSubject.next(updatedReminders);
    this.saveToStorage();

    return true;
  }

  /**
   * Remueve un recordatorio de un producto
   */
  removeReminder(productId: string): boolean {
    const currentReminders = this.remindersSubject.value;
    const updatedReminders = currentReminders.filter(
      (r) => r.productId !== productId
    );

    if (updatedReminders.length !== currentReminders.length) {
      this.remindersSubject.next(updatedReminders);
      this.saveToStorage();
      return true;
    }

    return false;
  }

  /**
   * Verifica si un producto tiene recordatorio
   */
  hasReminder(productId: string): boolean {
    return this.getReminderByProductId(productId) !== null;
  }

  /**
   * Obtiene el recordatorio de un producto
   */
  getReminderByProductId(productId: string): ProductReminder | null {
    return (
      this.remindersSubject.value.find((r) => r.productId === productId) || null
    );
  }

  /**
   * Obtiene todos los recordatorios
   */
  getReminders(): Observable<ProductReminder[]> {
    return this.remindersSubject.asObservable();
  }

  /**
   * Obtiene todos los recordatorios como array
   */
  getRemindersArray(): ProductReminder[] {
    return this.remindersSubject.value;
  }

  // ===== TRACKING DE VISTAS =====

  /**
   * Registra que un producto fue visto
   */
  logProductView(product: Producto): void {
    const productId = this.getProductUniqueId(product);
    const currentViews = this.viewsSubject.value;
    const existingViewIndex = currentViews.findIndex(
      (v) => v.productId === productId
    );

    if (existingViewIndex >= 0) {
      // Actualizar vista existente
      const existingView = currentViews[existingViewIndex];
      const updatedView: ProductViewLog = {
        ...existingView,
        viewedAt: new Date(),
        viewCount: existingView.viewCount + 1,
      };

      const updatedViews = [...currentViews];
      updatedViews[existingViewIndex] = updatedView;
      this.viewsSubject.next(updatedViews);
    } else {
      // Crear nueva vista
      const newView: ProductViewLog = {
        productId: productId,
        productSlug: this.generateProductSlug(product),
        productName: this.getComposedProductName(product),
        viewedAt: new Date(),
        viewCount: 1,
      };

      const updatedViews = [...currentViews, newView];
      this.viewsSubject.next(updatedViews);
    }

    this.saveToStorage();
  }

  /**
   * Obtiene el log de vistas de un producto
   */
  getProductViewLog(productId: string): ProductViewLog | null {
    return (
      this.viewsSubject.value.find((v) => v.productId === productId) || null
    );
  }

  /**
   * Obtiene todos los logs de vistas
   */
  getProductViews(): Observable<ProductViewLog[]> {
    return this.viewsSubject.asObservable();
  }

  /**
   * Obtiene todos los logs de vistas como array
   */
  getProductViewsArray(): ProductViewLog[] {
    return this.viewsSubject.value;
  }

  /**
   * Obtiene productos más vistos (ordenados por cantidad de vistas)
   */
  getMostViewedProducts(limit: number = 10): ProductViewLog[] {
    return this.viewsSubject.value
      .sort((a, b) => b.viewCount - a.viewCount)
      .slice(0, limit);
  }

  // ===== UTILIDADES =====

  /**
   * Limpia recordatorios antiguos (más de 30 días)
   */
  cleanOldReminders(): void {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const currentReminders = this.remindersSubject.value;
    const validReminders = currentReminders.filter(
      (r) => r.addedAt > thirtyDaysAgo
    );

    if (validReminders.length !== currentReminders.length) {
      this.remindersSubject.next(validReminders);
      this.saveToStorage();
    }
  }

  /**
   * Limpia logs de vistas antiguos (más de 90 días)
   */
  cleanOldViewLogs(): void {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const currentViews = this.viewsSubject.value;
    const validViews = currentViews.filter((v) => v.viewedAt > ninetyDaysAgo);

    if (validViews.length !== currentViews.length) {
      this.viewsSubject.next(validViews);
      this.saveToStorage();
    }
  }

  // ===== STORAGE =====

  private loadFromStorage(): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }

      // Cargar recordatorios
      const remindersData = localStorage.getItem(this.REMINDERS_STORAGE_KEY);
      if (remindersData) {
        const reminders = JSON.parse(remindersData).map((r: any) => ({
          ...r,
          addedAt: new Date(r.addedAt),
        }));
        this.remindersSubject.next(reminders);
      }

      // Cargar vistas
      const viewsData = localStorage.getItem(this.VIEWS_STORAGE_KEY);
      if (viewsData) {
        const views = JSON.parse(viewsData).map((v: any) => ({
          ...v,
          viewedAt: new Date(v.viewedAt),
        }));
        this.viewsSubject.next(views);
      }
    } catch (error) {
      console.error('Error loading tracking data from storage:', error);
    }
  }

  private saveToStorage(): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      localStorage.setItem(
        this.REMINDERS_STORAGE_KEY,
        JSON.stringify(this.remindersSubject.value)
      );
      localStorage.setItem(
        this.VIEWS_STORAGE_KEY,
        JSON.stringify(this.viewsSubject.value)
      );
    } catch (error) {
      console.error('Error saving tracking data to storage:', error);
    }
  }

  // ===== HELPERS =====

  /**
   * Obtiene un identificador único para el producto
   */
  private getProductUniqueId(product: Producto): string {
    // Usar SKU si está disponible, sino usar part_number
    return product.sku?.trim() || product.part_number;
  }

  private generateProductSlug(product: Producto): string {
    // Implementación simple del slug - puedes usar la del ProductService si está disponible
    return `${product.marca}-${product.descripcion}-${
      product.sku || product.part_number
    }`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private getComposedProductName(product: Producto): string {
    return `${product.marca} ${product.descripcion}`.trim();
  }
}
